import * as React from 'react';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/styles';
import { ToastContainer } from 'react-toastify';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import Analytics from 'react-router-ga';
import { loggedIn, logout } from '../../actions/auth';
import { fetchBuildings } from '../../actions/buildings';
import * as AuthApi from '../../api/auth';
import * as UsersApi from '../../api/users';

import 'font-awesome/css/font-awesome.css';
import '../../assets/css/bs.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-confirm-alert/src/react-confirm-alert.css';
import '../../assets/css/styles.css';

import * as Firebase from '../../firebase';
import { initAppConfig } from '../../config';

import Navbar from './navbar';
import SimpleNavbar from './simple-navbar';
import Routes from './routes';
import RegistrationRoutes from './registration-routes';
import UnderReviewRoutes from './under-review-routes';
import Loading from '../../components/Shared/Loading';
import SnackbarNotifications from '../../components/Shared/SnackbarNotifications';
import TosModal from '../Profile/tos';
import Authorize from '../Authorize';
import theme from '../../theme';
import { updateUserLanguage } from '../../actions/users';
import packageJson from '../../../package.json';

const getParameterByName = (name) => {
  name = name.replace(/[\[\]]/g, '\\$&');

  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
  const results = regex.exec(window.location.href);

  if (!results) {
    return null;
  }

  if (!results[2]) {
    return '';
  }

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

const styles = createStyles((theme) => ({
  container: {
    marginTop: '70px',
    marginBottom: '4%',

    [theme.breakpoints.down('xs')]: {
      marginTop: '56px',
    },
  },
}));

const Wrapper = ({ loggedIn, logout, user, updateLanguage, children }) => {
  if (loggedIn) {
    return (
      <Navbar logout={logout} user={user} updateLanguage={updateLanguage}>
        {children}
      </Navbar>
    );
  }

  return <SimpleNavbar updateLanguage={updateLanguage}>{children}</SimpleNavbar>;
};

export class AppContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      menu: false,
      loading: true,
    };

    if (localStorage.getItem('localVersion') !== packageJson.version) {
      localStorage.setItem('localVersion', packageJson.version);
      window.location.reload(true);
    }
  }

  fcm() {
    UsersApi.testFcm();
  }

  render() {
    const { isLoggedIn, user, classes } = this.props;
    const loggedIn = isLoggedIn && !!user;
    const { loading } = this.state;
    const shouldShowTosModal = user && (!user.tos || !user.privacy);

    const isRegistrationIncomplete = user && user.registration_step;
    const isUserUnderReview =
      user && !user.registration_step && !user.profile_approved_at && !!user.profile_completed_at;

    if (loading) {
      return null;
    }

    // OAuth consent screen: public, outside the app shell (no Navbar, tabs or
    // TOS modal) and outside Analytics. It comes before the registration and
    // review branches so their routes never swallow the request.
    if (this.props.location.pathname === '/authorize') {
      return (
        <ThemeProvider theme={theme}>
          <Authorize loggedIn={loggedIn} user={user} logout={this.logout} />
          <ToastContainer position="bottom-left" />
        </ThemeProvider>
      );
    }

    if (isRegistrationIncomplete) {
      return (
        <ThemeProvider theme={theme}>
          <SimpleNavbar
            loggedIn={loggedIn}
            logout={this.logout}
            updateLanguage={this.props.updateLanguage}
          >
            <div className={classes.container}>
              <RegistrationRoutes loggedIn={loggedIn} />
            </div>
          </SimpleNavbar>

          <Loading visible={this.props.loading} />
          <ToastContainer position="bottom-left" />
        </ThemeProvider>
      );
    }

    if (isUserUnderReview) {
      return (
        <ThemeProvider theme={theme}>
          <SimpleNavbar
            loggedIn={loggedIn}
            logout={this.logout}
            updateLanguage={this.props.updateLanguage}
          >
            <div className={classes.container}>
              <UnderReviewRoutes loggedIn={loggedIn} />
            </div>
          </SimpleNavbar>

          <Loading visible={this.props.loading} />
          <ToastContainer position="bottom-left" />
        </ThemeProvider>
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <Analytics id="UA-179334999-1" debug>
          <Wrapper
            loggedIn={loggedIn}
            logout={this.logout}
            updateLanguage={this.props.updateLanguage}
            user={user}
          >
            <div className={classes.container}>
              {shouldShowTosModal && <TosModal />}
              <Routes loggedIn={loggedIn} />
            </div>
          </Wrapper>
        </Analytics>
        <Loading visible={this.props.loading} />
        <ToastContainer position="bottom-left" />
        <SnackbarNotifications />
      </ThemeProvider>
    );
  }

  async componentDidMount() {
    await initAppConfig().catch(() => {});

    const token = getParameterByName('token');
    if (token) {
      localStorage.setItem('token', token);
      this.props.history.push(this.props.history.location.pathname);
    }

    const data = localStorage.getItem('token') ? await this.fetchCurrentUser() : null;

    if (data) {
      if (data.language) {
        this.props.i18n.changeLanguage(data.language);
      } else {
        const browserLang = navigator.language.substr(0, 2);
        const supportedLangs = ['en', 'pt', 'es', 'zh', 'fr'];
        const defaultLang = supportedLangs.includes(browserLang)
          ? browserLang
          : process.env.REACT_APP_DEFAULT_LANGUAGE?.toLowerCase() || 'en';
        this.props.i18n.changeLanguage(defaultLang);
      }

      this.props.loggedIn({
        token: localStorage.getItem('token'),
        user: data,
      });

      if (data.profile_approved_at) {
        this.props.fetchBuildings();
      }

      this.setState({ loading: false });

      if (data.should_change_password) {
        this.props.history.push('/app/change-password?redirected=1');
      }

      if (
        data.registration_step &&
        !this.props.history.location.pathname.includes('register/verify-email')
      ) {
        this.props.history.push('/app/complete-registration');
      }

      try {
        const fcmToken = await Firebase.getToken();
        const device_id = await AuthApi.getDeviceId();

        if (fcmToken) {
          UsersApi.fcm({ token: fcmToken, device_id });
        }
      } catch (err) {
        console.log(err);
      }
    } else {
      // For non-logged users, detect browser language
      const browserLang = navigator.language.substr(0, 2);
      const supportedLangs = ['en', 'pt', 'es', 'zh', 'fr'];
      const defaultLang = supportedLangs.includes(browserLang) ? browserLang : 'en';
      this.props.i18n.changeLanguage(defaultLang);

      this.setState({ loading: false });
    }
  }

  // On the consent screen a 401 must not go through the global handler, whose
  // reload to /login would drop the return path: the session is cleared here
  // and the screen sends the user to the login page keeping it.
  async fetchCurrentUser() {
    if (this.props.location.pathname !== '/authorize') {
      const { data } = await AuthApi.me();

      return data;
    }

    try {
      const { data } = await AuthApi.meRaw();

      return data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        this.props.logout(true);

        return null;
      }

      throw error;
    }
  }

  logout = async (force = false) => {
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister();
        });
      }

      await AuthApi.logout();
    } catch (err) {}

    const { history } = this.props;
    const isOnPrivatePage = history.location.pathname.startsWith('/app');

    this.props.logout(force);

    // PrivateRoute would keep the current page as the post-login return path.
    // A logout (manual or by inactivity) is not a "come back here" visit and the
    // next person to log in may be someone else, so land on a plain /login and
    // let the next login go to /app as before.
    if (isOnPrivatePage) {
      history.replace('/login');
    }
  };
}

const mapStateToProps = (state) => ({
  isLoggedIn: state.auth.loggedIn,
  user: state.auth.user,
  loading: state.ui.loading > 0,
});

const mapDispatchToProps = (dispatch) => ({
  loggedIn: (data) => dispatch(loggedIn(data)),
  logout: (f) => dispatch(logout(f)),
  fetchBuildings: () => dispatch(fetchBuildings()),
  updateLanguage: (language) => dispatch(updateUserLanguage(language)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withStyles(styles)(AppContainer)))
);
